import { useNavigate } from "@tanstack/react-router";
import type { ComponentProps, MouseEvent } from "react";

import { toPublicPath, useForgePathSegment } from "../lib/forgePath.js";

/** A click the browser should handle itself: another tab or window, a download, a modifier. */
function isBrowserClick(event: MouseEvent<HTMLAnchorElement>, target: string | undefined) {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    (target !== undefined && target !== "_self")
  );
}

/**
 * The design system's link component (its LinkProvider): a plain anchor whose in-app `href`
 * navigates through the router, so a nav link, a tile or a footer link changes the page in place.
 * An href off this origin, or a click the browser owns, is left to the browser. The anchor shows
 * the address the page answers at, so a forge path reads the store's word for it
 * (`lib/forgePath.ts`) on hover, in a new tab and when copied.
 */
export function RouterLink({ href, onClick, target, ...props }: ComponentProps<"a">) {
  const navigate = useNavigate();
  const forgeSegment = useForgePathSegment();
  return (
    <a
      {...props}
      href={href === undefined ? undefined : toPublicPath(href, forgeSegment)}
      target={target}
      onClick={(event) => {
        onClick?.(event);
        if (!href?.startsWith("/") || href.startsWith("//") || isBrowserClick(event, target))
          return;
        event.preventDefault();
        void navigate({ href });
      }}
    />
  );
}
